import { useService } from '@nzyme/vue-ioc/useService.js';
import { computed, ref, toValue, watch } from 'vue';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
import { getChatNextEventSeq } from '@sigma/chat-common/utils/getChatNextEventSeq.js';
import { getPendingAgentMessage } from '@sigma/chat-common/utils/getPendingAgentMessage.js';
import { buildChatMessageEvent } from './buildChatMessageEvent.js';
import { ChatPendingMessageStore } from './ChatPendingMessageStore.js';
import { useAnswerChatQuestion } from './useAnswerChatQuestion.js';
/**
 * The single composable behind the chat input. Lets one text field double as both a message
 * composer and the answer surface for a pending free-text agent question, and owns all of the
 * per-chat bookkeeping that used to be split across three composables:
 *
 *   - the `message` ref + draft autosave + failed-send restore/auto-retry (was `useSendChatMessage`)
 *   - pending-text-question detection + message-vs-answer routing (was `useComposerSend`)
 *   - the optimistic answer submit (delegated to {@link useAnswerChatQuestion})
 *
 * `target` (the audience, a constant) drives the eagerly-read `isAnswering` computed; `author`
 * (identity, possibly carrying a staff id) is resolved only at send time so auth is never touched
 * during render. The actor + draft store are resolved from IoC, so call sites only describe *who*
 * is composing and *how* to mark read.
 */
export function useChatComposer(params) {
    const pendingMessage = useService(ChatPendingMessageStore);
    const chatActorRef = useService(ActorClient).actor(ChatActor);
    // The answer path reuses the same optimistic-submit primitive the inline question widgets use.
    const { submit: submitAnswer } = useAnswerChatQuestion({
        chat: params.chat,
        chatId: params.chatId,
        author: params.author,
        markRead: params.markRead,
    });
    const message = ref('');
    // File metadata stashed from a failed send attempt, used during retry when the original
    // in-memory file state is no longer available (e.g. page reload). Lets the optimistic event
    // render file previews while the retry is in flight; file IDs for the payload derive from this.
    const pendingFiles = ref();
    // Guard suppressing the draft-autosave watcher while we programmatically restore a
    // failed-to-send message in restorePendingMessage. Without this, the synchronous
    // `message.value = failed` fires the watcher and kicks off a fire-and-forget setDraft that can
    // race the subsequent clearAll — leaving stale text in IndexedDB.
    const restoringFailed = ref(false);
    let restored = false;
    // The agent's latest turn the user can respond to here — a question or a plain message.
    // includeMessages keeps the input available for a message reply even while the agent is active.
    // Reads the plain `target` (never `author`) so it stays auth-free and safe to evaluate during the
    // pre-load render.
    const pendingAgentMessage = computed(() => params.chat.loaded
        ? getPendingAgentMessage(params.chat.events, { target: params.target, includeMessages: true })
        : undefined);
    // Answer mode is only for free-text questions — structured questions keep their inline widgets,
    // and a plain message uses the normal send path.
    const pendingTextQuestion = computed(() => {
        const pending = pendingAgentMessage.value;
        return pending?.type === 'AGENT_QUESTION' && pending.input.type === 'TEXT' ? pending : undefined;
    });
    const isAnswering = computed(() => pendingTextQuestion.value != null);
    const canReplyToMessage = computed(() => pendingAgentMessage.value?.type === 'AGENT_MESSAGE');
    // Auto-save draft on every change. Skip while restorePendingMessage primes the input with a
    // failed-to-send message (the follow-up clearAll would otherwise race this setDraft).
    // Fire-and-forget — losing a draft on tab-close mid-typing is acceptable. `flush: 'sync'` so the
    // restoringFailed guard — set synchronously around the assignment — is observed when it runs.
    watch(message, value => {
        if (restoringFailed.value) {
            return;
        }
        void pendingMessage.setDraft(params.chatId, value);
    }, { flush: 'sync' });
    // Restore once the chat has loaded so any auto-retry calculates its seq against the actual
    // event history. `immediate: true` handles the seed-before-mount case where `loaded` is already
    // true (e.g. doctor-start-chat seeds the chat).
    watch(() => params.chat.loaded, loaded => {
        if (!loaded || restored) {
            return;
        }
        restored = true;
        void restorePendingMessage();
    }, { immediate: true });
    return { message, isAnswering, canReplyToMessage, send };
    /**
     * Routes a composer send. When a pending free-text question exists, the composer answers it
     * (clearing the draft *before* the await so a successful answer can never resurface as a stale
     * message draft); otherwise it sends a normal chat message. The two paths keep deliberately
     * different rollback shapes — see {@link sendMessage} (failed-stash) vs the answer branch.
     */
    async function send() {
        const question = pendingTextQuestion.value;
        if (!question) {
            await sendMessage();
            return;
        }
        const value = message.value.trim();
        if (!value) {
            return;
        }
        message.value = '';
        // Clear the persisted draft before the await so a successful answer can never be restored
        // later as a normal message draft (the `message` ref autosaves on every keystroke).
        await pendingMessage.clearAll(params.chatId);
        try {
            await submitAnswer({ type: 'TEXT', value }, question.seq);
        }
        catch (error) {
            // Restore the text so the user can retry instead of losing their answer.
            message.value = value;
            throw error;
        }
    }
    async function sendMessage() {
        const messageValue = message.value.trim();
        // Submit files if the caller provided file upload integration.
        let fileIds;
        let files;
        if (params.filesUpload) {
            const result = await params.filesUpload.submitFiles();
            if (!result.valid) {
                return;
            }
            if (result.fileIds.length > 0) {
                fileIds = result.fileIds;
                files = result.files;
            }
        }
        // Use pending files from a failed retry if no fresh files were provided.
        if (!fileIds?.length && pendingFiles.value?.length) {
            files = pendingFiles.value;
            fileIds = files.map(f => f.id);
        }
        // Need either a message or files to proceed.
        if (!messageValue && !fileIds?.length) {
            return;
        }
        message.value = '';
        // Network-send failures must trigger the rollback/retry path: repopulate the input and stash
        // the text as "failed" so the next mount auto-retries.
        try {
            await pushAndSendMessage(messageValue, fileIds, files);
        }
        catch (error) {
            message.value = messageValue;
            await pendingMessage.setFailedMessage(params.chatId, messageValue, files);
            throw error;
        }
        // Clear pending file state only after the send completes successfully. Clearing before would
        // lose the data on double failure (send throws AND setFailedMessage also fails), making the
        // next retry send the message without files.
        pendingFiles.value = undefined;
        // Atomic clear: a two-call sequence could leave a stale draft behind if the first write
        // succeeded and the second failed. Storage errors here propagate to the app-level Sentry
        // handler — no nested try/catch.
        await pendingMessage.clearAll(params.chatId);
        // Clear files on success so the UI file picker is reset.
        params.filesUpload?.clearFiles();
    }
    async function pushAndSendMessage(messageValue, fileIds, files) {
        const seq = getChatNextEventSeq(params.chat.events);
        const event = buildChatMessageEvent({
            author: toValue(params.author),
            chatId: params.chatId,
            seq,
            content: messageValue,
            files,
        });
        // Push optimistic local event synchronously so the UI updates immediately. `markRead` is
        // deferred until after the request resolves — persisting the read pointer for an event that
        // never lands server-side would let a later remote event reuse the same seq and be treated
        // as already read, suppressing unread notifications.
        params.chat.pushLocalEvent(event);
        const payload = fileIds ? { message: messageValue, fileIds } : { message: messageValue };
        try {
            await chatActorRef.requests.sendMessage(params.chatId, payload);
        }
        catch (error) {
            await params.chat.removeLocalEvents();
            throw error;
        }
        params.markRead(params.chatId, seq);
    }
    /**
     * On entry, restore a failed-to-send message (auto-retry once) or a draft. Failed takes
     * precedence — it represents an unfinished send round-trip. The draft is only loaded when no
     * failed message is pending.
     */
    async function restorePendingMessage() {
        const failedData = await pendingMessage.getFailedMessage(params.chatId);
        if (failedData) {
            // Suppress the draft-autosave watcher during the assignment — sendMessage will clearAll,
            // and a racing setDraft from the watcher could land after, leaving a stale draft.
            restoringFailed.value = true;
            try {
                message.value = failedData.message;
            }
            finally {
                restoringFailed.value = false;
            }
            if (failedData.files?.length) {
                pendingFiles.value = failedData.files;
            }
            await pendingMessage.clearFailedMessage(params.chatId);
            await sendMessage();
            return;
        }
        const draft = await pendingMessage.getDraft(params.chatId);
        if (draft) {
            message.value = draft;
        }
    }
}
//# sourceMappingURL=useChatComposer.js.map